import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import apiConfig from 'constants/apiConfig';
import { parseError } from 'hooks/shared/error';
import { toast } from 'react-toastify';

export type AorCoverageRow = {
  userGuid: string;
  aorItemId: string;
};

/**
 * Which areas each member of the organization covers.
 *
 * Fetched for the whole organization at once because the member table renders
 * every row's coverage together, and geo-targeted sends are only as accurate as
 * this mapping.
 */
export const useAorCoverage = (
  groupGuid: string | undefined,
  options?: { enabled?: boolean }
) => {
  return useQuery<AorCoverageRow[]>({
    queryKey: ['aorCoverage', groupGuid],
    enabled: Boolean(groupGuid) && options?.enabled !== false,
    queryFn: async () => {
      const response = await axios.get(
        apiConfig.apiGateway.aorCoverage(groupGuid as string)
      );
      return response.data;
    },
  });
};

/** Replaces one member's coverage with exactly the areas given. */
export const useSetAorCoverage = (groupGuid: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values: { userGuid: string; aorItemIds: string[] }) => {
      const response = await axios.put(
        apiConfig.apiGateway.userAorCoverage(
          groupGuid as string,
          values.userGuid
        ),
        { aorItemIds: values.aorItemIds }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['aorCoverage', groupGuid] });
    },
    onError: (error) => {
      toast.error(parseError(error));
    },
  });
};
